import { useEffect, useMemo, useRef, useState } from "react";

import type { ReceivedGumball, ThemeSceneProps } from "../types";

const PATH_VIEW_W = 1000;
const PATH_VIEW_H = 600;
const RAINBOW_PATH = "M 1010 470 C 820 460, 660 540, 460 540 S 100 510, -10 560";

const STAR_FIELD = buildStars(110);
const HILLS = buildHills();
const TREES = buildTrees();
const LOLLIPOPS = buildLollipops();
const SHOOTING_STARS = buildShootingStars();
const FLOATING_EMOJIS = buildFloatingEmojis();
const SPRINKLES = buildSprinkles(34);

const RAINBOW_BANDS: Array<{ color: string; offset: number; width: number }> = [
  { color: "#ff4fd8", offset: -22, width: 5 },
  { color: "#ff8c00", offset: -14, width: 5 },
  { color: "#ffd700", offset: -7, width: 5 },
  { color: "#c8ff00", offset: 0, width: 5 },
  { color: "#00e5ff", offset: 7, width: 5 },
  { color: "#c044ff", offset: 14, width: 5 },
  { color: "#ff4fd8", offset: 22, width: 5 }
];

const RAINBOW_PULSES = [
  { color: "#ffffff", duration: 5, delay: 0, width: 6, opacity: 0.95 },
  { color: "#ffd700", duration: 7, delay: -2, width: 5, opacity: 0.85 },
  { color: "#00e5ff", duration: 6, delay: -4, width: 5, opacity: 0.85 }
];

const GUMBALL_COLORS: ReadonlyArray<readonly [string, string]> = [
  ["#ff4fd8", "#ffd700"],
  ["#00e5ff", "#c8ff00"],
  ["#ffd700", "#ff8c00"],
  ["#c044ff", "#ff4fd8"],
  ["#00e5a0", "#ffd700"],
  ["#ff8c00", "#ff3a6e"]
];

const CARD_SPEED = 0.05;

export function StardustRainbowRoadScene({ received }: ThemeSceneProps) {
  const pathRef = useRef<SVGPathElement>(null);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const cardTRef = useRef<number[]>([]);
  const [pathLength, setPathLength] = useState(0);

  useEffect(() => {
    cardTRef.current = received.map((_, i) =>
      received.length === 0 ? 0 : i / received.length
    );
    cardRefs.current.length = received.length;
  }, [received.length]);

  useEffect(() => {
    const path = pathRef.current;
    if (path === null) {
      return;
    }
    const apply = () => setPathLength(path.getTotalLength());
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  useEffect(() => {
    const path = pathRef.current;
    if (path === null) {
      return;
    }
    let total = path.getTotalLength();
    let last = performance.now();
    let raf = 0;

    const onResize = () => {
      total = path.getTotalLength();
    };
    window.addEventListener("resize", onResize);

    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const ts = cardTRef.current;
      for (let i = 0; i < ts.length; i++) {
        ts[i] = (ts[i]! + CARD_SPEED * dt) % 1;
        const point = path.getPointAtLength(ts[i]! * total);
        const el = cardRefs.current[i];
        if (el !== null && el !== undefined) {
          el.style.left = `${(point.x / PATH_VIEW_W) * 100}%`;
          el.style.top = `${(point.y / PATH_VIEW_H) * 100}%`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [received.length]);

  const pulseDashArray = pathLength > 0 ? `${pathLength * 0.06} ${pathLength * 0.94}` : undefined;
  const pulseDashEnd = pathLength > 0 ? -pathLength : undefined;

  return (
    <div className="rr-scene" aria-hidden="true">
      <div className="rr-scene__sky" />
      <div className="rr-scene__stars">
        {STAR_FIELD.map((star, i) => (
          <span
            key={i}
            className={`rr-scene__star rr-scene__star--${star.style}`}
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: star.color,
              boxShadow: `0 0 ${Math.max(4, star.size * 2)}px ${star.color}`,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`
            }}
          />
        ))}
      </div>

      <div className="ad-scene__shooting-stars">
        {SHOOTING_STARS.map((s, i) => (
          <span
            key={i}
            className="ad-shooting-star"
            style={
              {
                top: `${s.top}%`,
                left: `${s.left}%`,
                background: `linear-gradient(90deg, transparent 0%, ${s.color}55 30%, ${s.color}cc 70%, #ffffff 100%)`,
                boxShadow: `0 0 14px ${s.color}, 0 0 4px #ffffff`,
                ["--ad-shoot-angle" as string]: `${s.angle}deg`,
                animationDelay: `${s.delay}s`,
                animationDuration: `${s.duration}s`
              } as React.CSSProperties
            }
          />
        ))}
      </div>

      <svg
        className="rr-scene__hills"
        viewBox="0 0 1000 600"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="srr-hill-back" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4a1880" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#1a0a3a" stopOpacity="0.95" />
          </linearGradient>
          <linearGradient id="srr-hill-mid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e96bd4" />
            <stop offset="100%" stopColor="#7a2a8a" />
          </linearGradient>
          <linearGradient id="srr-hill-front" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffb0d8" />
            <stop offset="100%" stopColor="#c044a8" />
          </linearGradient>
        </defs>
        {HILLS.map((hill, i) => (
          <path key={i} d={hill.d} fill={`url(#${hill.fill})`} opacity={hill.opacity} />
        ))}
      </svg>

      <svg
        className="rr-scene__rainbow"
        viewBox={`0 0 ${PATH_VIEW_W} ${PATH_VIEW_H}`}
        preserveAspectRatio="none"
      >
        <defs>
          <filter id="srr-rainbow-glow" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d={RAINBOW_PATH}
          stroke="rgba(255, 79, 216, 0.5)"
          strokeWidth="64"
          fill="none"
          strokeLinecap="round"
          filter="url(#srr-rainbow-glow)"
        />
        <path
          d={RAINBOW_PATH}
          stroke="#ff1cb1"
          strokeWidth="40"
          fill="none"
          strokeLinecap="round"
        />
        {RAINBOW_BANDS.map((band, i) => (
          <path
            key={i}
            d={RAINBOW_PATH}
            stroke={band.color}
            strokeWidth={band.width}
            fill="none"
            strokeLinecap="round"
            transform={`translate(0 ${band.offset})`}
            opacity="0.92"
          />
        ))}
        <path ref={pathRef} d={RAINBOW_PATH} stroke="none" fill="none" />
        {pulseDashArray !== undefined &&
          RAINBOW_PULSES.map((pulse, i) => (
            <path
              key={i}
              d={RAINBOW_PATH}
              stroke={pulse.color}
              strokeWidth={pulse.width}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={pulseDashArray}
              opacity={pulse.opacity}
              style={
                {
                  ["--rr-dash-end" as string]: `${pulseDashEnd}`,
                  animation: `rr-pulse ${pulse.duration}s linear infinite`,
                  animationDelay: `${pulse.delay}s`
                } as React.CSSProperties
              }
            />
          ))}
      </svg>

      <div className="rr-scene__props">
        {TREES.map((tree, i) => (
          <CandyTree key={i} {...tree} index={i} />
        ))}
        {LOLLIPOPS.map((lp, i) => (
          <Lollipop key={i} {...lp} index={i} />
        ))}
      </div>

      <div className="ad-scene__sprinkles">
        {SPRINKLES.map((s, i) => (
          <span
            key={i}
            className="ad-scene__sprinkle"
            style={{
              left: `${s.x}%`,
              backgroundColor: s.color,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`
            }}
          />
        ))}
      </div>

      <div className="ad-scene__floating-emojis">
        {FLOATING_EMOJIS.map((e, i) => (
          <span
            key={i}
            className="ad-float-emoji"
            style={{
              left: `${e.left}%`,
              fontSize: `${e.size}px`,
              animationDelay: `${e.delay}s`,
              animationDuration: `${e.duration}s`
            }}
          >
            {e.glyph}
          </span>
        ))}
      </div>

      <PrismUnicorn />

      <div className="rr-scene__cards">
        {received.map((entry, i) => {
          const palette = GUMBALL_COLORS[i % GUMBALL_COLORS.length]!;
          return (
            <GumballCard
              key={entry.id}
              entry={entry}
              colors={palette}
              index={i}
              elRef={(el) => {
                cardRefs.current[i] = el;
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function PrismUnicorn() {
  return (
    <div className="pc-mascot" aria-hidden="true">
      <div className="pc-mascot__bob">
        <img
          className="pc-mascot__image"
          src="/assets/prism-candyland-unicorn.png"
          alt=""
          draggable={false}
        />
      </div>
    </div>
  );
}

function GumballCard({
  entry,
  colors,
  index,
  elRef
}: {
  entry: ReceivedGumball;
  colors: readonly [string, string];
  index: number;
  elRef: (el: HTMLDivElement | null) => void;
}) {
  const count = Math.max(1, Math.min(entry.amount, 8));
  const columns = Math.min(count, 4);
  const pieces = useMemo(() => Array.from({ length: count }, (_, i) => i), [count]);

  return (
    <div ref={elRef} className="rr-card">
      <div
        className="rr-card__bob"
        style={{ animationDelay: `${(index % 5) * -0.7}s` }}
      >
        <div
          className="rr-card__cluster"
          style={{ gridTemplateColumns: `repeat(${columns}, 18px)` }}
        >
          {pieces.map((i) => (
            <Gumball
              key={i}
              from={colors[0]}
              to={colors[1]}
              variant={(index + i) % 4}
              index={index * 7 + i}
            />
          ))}
        </div>
        <div className="rr-card__tooltip" role="tooltip">
          <span className="rr-card__from">{entry.from}</span>
          <span className="rr-card__amount">{entry.amount} 🍬</span>
          {entry.reason !== null && entry.reason.trim() !== "" && (
            <div className="rr-card__note">{entry.reason}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Gumball({
  from,
  to,
  variant,
  index
}: {
  from: string;
  to: string;
  variant: number;
  index: number;
}) {
  const id = `srr-gb-${index}`;
  const accent = variant % 2 === 0 ? to : from;
  return (
    <svg className="rr-card__piece" width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
      <defs>
        <radialGradient id={id} cx="35%" cy="30%" r="65%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
          <stop offset="35%" stopColor={accent} />
          <stop offset="100%" stopColor={from} />
        </radialGradient>
      </defs>
      <circle cx="10" cy="10" r="9" fill={`url(#${id})`} />
      {variant === 0 && (
        <circle cx="10" cy="10" r="9" fill="none" stroke={to} strokeWidth="0.4" opacity="0.35" />
      )}
      {variant === 1 && (
        <ellipse cx="10" cy="10" rx="9" ry="3" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.6" />
      )}
      {variant === 2 && (
        <path d="M3 10 Q10 6 17 10" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" />
      )}
      <circle cx="7" cy="6.5" r="2.4" fill="rgba(255,255,255,0.6)" />
    </svg>
  );
}

function CandyTree({
  left,
  bottom,
  scale,
  hue,
  index
}: {
  left: number;
  bottom: number;
  scale: number;
  hue: number;
  index: number;
}) {
  const petalCol = `hsla(${hue}, 80%, 75%, 0.92)`;
  const petalCol2 = `hsla(${(hue + 340) % 360}, 65%, 60%, 0.88)`;
  const id = `srr-tree-${index}`;
  const w = 160;
  const h = 200;
  const tw = Math.round(w * 0.09);

  const petals = useMemo(() => {
    const out: Array<{
      left: number;
      duration: number;
      delay: number;
      size: number;
      colorIdx: number;
      kind: number;
    }> = [];
    let s = 0xa11ce + index * 9181;
    for (let i = 0; i < 12; i++) {
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      const px = (s % 2000) / 1000 - 0.5;
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      const dur = 3 + (s % 4000) / 1000;
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      const delay = -((s % 8000) / 1000);
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      const size = 4 + (s % 5000) / 1000;
      s = (s * 1103515245 + 12345) & 0x7fffffff;
      const colorIdx = s % 2;
      out.push({ left: px, duration: dur, delay, size, colorIdx, kind: i % 3 });
    }
    return out;
  }, [index]);

  return (
    <div
      className="rr-tree"
      style={{
        left: `${left}%`,
        bottom: `${bottom}%`,
        transform: `scale(${scale})`
      }}
    >
      <div
        className="rr-tree__bob"
        style={{ animationDelay: `${index * -0.8}s` }}
      >
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" style={{ overflow: "visible" }}>
        <defs>
          <filter id={`${id}-glow`} x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="2.4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id={`${id}-trunk`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(180,220,255,0.85)" />
            <stop offset="35%" stopColor="rgba(255,255,255,0.95)" />
            <stop offset="65%" stopColor="rgba(220,180,255,0.85)" />
            <stop offset="100%" stopColor="rgba(180,255,240,0.78)" />
          </linearGradient>
        </defs>
        <rect
          x={(w - tw) / 2}
          y={h * 0.48}
          width={tw}
          height={h * 0.55}
          rx={tw * 0.4}
          fill={`url(#${id}-trunk)`}
          filter={`url(#${id}-glow)`}
        />
        <line x1={w * 0.5} y1={h * 0.5} x2={w * 0.12} y2={h * 0.22} stroke="rgba(200,230,255,0.75)" strokeWidth={tw * 0.8} strokeLinecap="round" />
        <line x1={w * 0.5} y1={h * 0.5} x2={w * 0.88} y2={h * 0.18} stroke="rgba(220,200,255,0.75)" strokeWidth={tw * 0.8} strokeLinecap="round" />
        <line x1={w * 0.5} y1={h * 0.5} x2={w * 0.5} y2={h * 0.08} stroke="rgba(255,240,200,0.7)" strokeWidth={tw * 0.7} strokeLinecap="round" />
        <line x1={w * 0.25} y1={h * 0.3} x2={w * 0.05} y2={h * 0.38} stroke="rgba(255,255,255,0.5)" strokeWidth={tw * 0.5} strokeLinecap="round" />
        <line x1={w * 0.78} y1={h * 0.26} x2={w * 0.95} y2={h * 0.35} stroke="rgba(255,255,255,0.5)" strokeWidth={tw * 0.5} strokeLinecap="round" />
        <circle cx={w * 0.55} cy={h * 0.08} r={w * 0.28} fill={petalCol} opacity="0.78" />
        <circle cx={w * 0.25} cy={h * 0.22} r={w * 0.22} fill={petalCol2} opacity="0.72" />
        <circle cx={w * 0.75} cy={h * 0.18} r={w * 0.2} fill={petalCol} opacity="0.7" />
        <circle cx={w * 0.42} cy={h * 0.12} r={w * 0.18} fill={petalCol2} opacity="0.65" />
        <circle cx={w * 0.5} cy={h * 0.18} r={w * 0.24} fill="rgba(255,200,230,0.5)" opacity="0.6" />
        <circle cx={w * 0.5} cy={h * 0.1} r={w * 0.28} fill="none" stroke="rgba(255,240,248,0.35)" strokeWidth="1.5" />
      </svg>
      {petals.map((p, i) => (
        <span
          key={i}
          className={`rr-tree__petal rr-tree__petal--${p.kind}`}
          style={{
            left: `${50 + p.left * 90}%`,
            width: `${p.size}px`,
            height: `${p.size * 0.6}px`,
            background: p.colorIdx === 0 ? petalCol : petalCol2,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`
          }}
        />
      ))}
      </div>
    </div>
  );
}

function Lollipop({
  left,
  bottom,
  scale,
  hue,
  index
}: {
  left: number;
  bottom: number;
  scale: number;
  hue: number;
  index: number;
}) {
  const col = `hsl(${hue}, 90%, 65%)`;
  const col2 = `hsl(${(hue + 60) % 360}, 90%, 70%)`;
  const sz = 70;
  const headStyle = {
    width: `${sz}px`,
    height: `${sz}px`,
    background: `conic-gradient(${col} 0deg, ${col2} 45deg, ${col} 90deg, ${col2} 135deg, ${col} 180deg, ${col2} 225deg, ${col} 270deg, ${col2} 315deg, ${col} 360deg)`,
    boxShadow: `0 0 ${sz * 0.55}px ${col}cc, inset -${sz * 0.08}px -${sz * 0.08}px ${sz * 0.16}px rgba(0,0,0,0.22), inset ${sz * 0.06}px ${sz * 0.06}px ${sz * 0.12}px rgba(255,255,255,0.55)`
  };
  const stickStyle = {
    width: `${sz * 0.1}px`,
    height: `${sz * 1.9}px`,
    background: `linear-gradient(180deg, rgba(255,255,255,0.75), ${col2})`
  };
  return (
    <div
      className="rr-lolli"
      style={{
        left: `${left}%`,
        bottom: `${bottom}%`,
        transform: `scale(${scale})`
      }}
    >
      <div
        className="rr-lolli__bob"
        style={{ animationDelay: `${index * -0.5}s` }}
      >
        <div className="rr-lolli__head" style={headStyle} />
        <div className="rr-lolli__stick" style={stickStyle} />
      </div>
    </div>
  );
}

function buildStars(count: number) {
  const out: Array<{
    x: number;
    y: number;
    size: number;
    delay: number;
    duration: number;
    color: string;
    style: "dot" | "big" | "tint";
  }> = [];
  const COLORS = ["#ffffff", "#ffd700", "#ff4fd8", "#a8e0ff", "#c8ff00"];
  let seed = 0xc0ffee;
  for (let i = 0; i < count; i++) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const x = (seed % 1000) / 10;
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const y = (seed % 700) / 10;
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const sizeRoll = seed % 100;
    const size = sizeRoll < 70 ? 1 + (sizeRoll % 20) / 10 : 2.5 + (sizeRoll % 40) / 10;
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const delay = -((seed % 50) / 10);
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const duration = 1.6 + (seed % 30) / 10;
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const color = COLORS[seed % COLORS.length]!;
    const style = sizeRoll < 70 ? "dot" : sizeRoll < 90 ? "tint" : "big";
    out.push({ x, y, size, delay, duration, color, style });
  }
  return out;
}

function buildHills() {
  return [
    {
      d: "M0 460 C 200 380, 360 420, 520 410 S 880 460, 1000 410 L 1000 600 L 0 600 Z",
      fill: "srr-hill-back",
      opacity: 0.9
    },
    {
      d: "M0 510 C 220 440, 380 475, 560 470 S 880 510, 1000 470 L 1000 600 L 0 600 Z",
      fill: "srr-hill-mid",
      opacity: 0.85
    },
    {
      d: "M0 565 C 220 510, 380 535, 560 535 S 880 575, 1000 540 L 1000 600 L 0 600 Z",
      fill: "srr-hill-front",
      opacity: 0.95
    }
  ];
}

function buildTrees() {
  return [
    { left: 14, bottom: 14, scale: 1.05, hue: 320 },
    { left: 36, bottom: 22, scale: 1.3, hue: 300 },
    { left: 60, bottom: 16, scale: 1.15, hue: 330 }
  ];
}

function buildLollipops() {
  return [
    { left: 6, bottom: 8, scale: 0.95, hue: 320 },
    { left: 26, bottom: 6, scale: 1.05, hue: 50 },
    { left: 48, bottom: 5, scale: 1.15, hue: 200 },
    { left: 72, bottom: 9, scale: 1.1, hue: 280 },
    { left: 88, bottom: 6, scale: 1.0, hue: 160 }
  ];
}

function buildShootingStars() {
  return [
    { top: 4, left: -10, angle: 24, color: "#ff6b9d", duration: 3.5, delay: 0 },
    { top: 12, left: -8, angle: 28, color: "#4ec5ff", duration: 5.2, delay: -2.4 },
    { top: 0, left: -12, angle: 22, color: "#ffd23f", duration: 4.1, delay: -1.1 },
    { top: 18, left: -10, angle: 32, color: "#b88aff", duration: 6.0, delay: -3.7 },
    { top: 8, left: -10, angle: 26, color: "#7bd685", duration: 4.8, delay: -5.3 }
  ];
}

function buildFloatingEmojis() {
  const glyphs = ["✨", "🌈", "⭐", "💫", "✨", "💖", "🌟", "✨"];
  const out: Array<{
    glyph: string;
    left: number;
    size: number;
    delay: number;
    duration: number;
  }> = [];
  let s = 0xc0de;
  for (let i = 0; i < glyphs.length; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const left = (s % 1000) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const size = 18 + (s % 14);
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const duration = 8 + (s % 60) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const delay = -((s % 100) / 10);
    out.push({ glyph: glyphs[i]!, left, size, delay, duration });
  }
  return out;
}

function buildSprinkles(count: number) {
  const out: Array<{
    x: number;
    delay: number;
    duration: number;
    color: string;
  }> = [];
  const COLORS = ["#ff6b9d", "#ff9c4a", "#ffd23f", "#7bd685", "#4ec5ff", "#b88aff", "#ffffff"];
  let s = 0xfeed;
  for (let i = 0; i < count; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const x = (s % 1000) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const duration = 5 + (s % 60) / 10;
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const delay = -((s % 100) / 10);
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const color = COLORS[s % COLORS.length]!;
    out.push({ x, delay, duration, color });
  }
  return out;
}
